import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ingestarPqrCrm,
  obtenerPerfilCliente,
  obtenerPqrCrm,
} from "@/lib/api";
import type { CrmPqrDetalleResponse, PerfilClienteWsResponse } from "@/lib/api";
import { formatearCop, formatearFechaCorta } from "@/lib/utils";
import { Header } from "@/components/Header";

export function PqrCrmDetallePage() {
  const { rad_open = "" } = useParams();
  const navigate = useNavigate();
  const [errorIngesta, setErrorIngesta] = useState<string | null>(null);

  const detalleQ = useQuery({
    queryKey: ["crm-detalle", rad_open],
    queryFn: () => obtenerPqrCrm(rad_open),
    enabled: !!rad_open,
  });

  const perfilQ = useQuery({
    queryKey: ["crm-perfil", detalleQ.data?.nic, detalleQ.data?.fecha_radicacion],
    queryFn: () =>
      obtenerPerfilCliente(
        detalleQ.data!.nic!,
        (detalleQ.data!.fecha_radicacion ?? "").slice(0, 10) || undefined
      ),
    enabled: !!detalleQ.data?.nic,
  });

  const ingestar = useMutation({
    mutationFn: () => ingestarPqrCrm(rad_open),
    onSuccess: (exp) => {
      navigate(`/expedientes/${exp.expediente_id}`);
    },
    onError: (err) => {
      setErrorIngesta((err as Error).message ?? "Error al ingresar al pipeline.");
    },
  });

  if (detalleQ.isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
          <p className="text-slate-500">Cargando radicado del CRM…</p>
        </main>
      </div>
    );
  }
  if (detalleQ.error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
          <div className="card p-4 border-rose-200 bg-rose-50 text-rose-700">
            {(detalleQ.error as Error).message}
          </div>
          <Link to="/pqr/crm" className="btn-secondary mt-4 inline-flex">
            ← Volver al listado
          </Link>
        </main>
      </div>
    );
  }
  const det = detalleQ.data!;
  const perfil = perfilQ.data;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="page">
        <div className="crumbs">
          <Link to="/">Pendientes CRM</Link>
          <span className="sep">/</span>
          <span className="mono">{det.rad_open}</span>
        </div>

        <div className="page-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {det.medio_atencion ?? "—"} · {det.tipo_pqr ?? "sin tipología"}
            </div>
            <h1 className="mono">{det.rad_open}</h1>
            <p className="sub">
              Pre-ingesta manual del CRM. GEMA hará clasificación, decisión
              y carta automáticamente al confirmar.
            </p>
          </div>
          <button
            onClick={() => {
              setErrorIngesta(null);
              ingestar.mutate();
            }}
            disabled={ingestar.isPending}
            className="btn btn--accent btn--lg"
          >
            {ingestar.isPending ? "Ingestando…" : "📥 Ingestar al motor GEMA"}
          </button>
        </div>

        {errorIngesta && (
          <div className="banner banner--red" style={{ marginBottom: 16 }}>
            <span className="banner__icon">⚠</span>
            <div>{errorIngesta}</div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <BloqueCrm det={det} />
          <BloquePerfilWs perfil={perfil} cargando={perfilQ.isLoading} />
        </div>

        <BloqueTextoReclamo det={det} />
      </main>
    </div>
  );
}

function BloqueCrm({ det }: { det: CrmPqrDetalleResponse }) {
  return (
    <div className="card">
      <div className="card__head">
        <div className="icon">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 5h10M3 8h10M3 11h6" strokeLinecap="round" />
          </svg>
        </div>
        <h3>Datos del CRM (Mercurio)</h3>
      </div>
      <div className="card__body">
        <dl className="kv">
          <dt>Cliente</dt>     <dd>{det.cliente ?? <span className="muted">—</span>}</dd>
          <dt>NIC</dt>          <dd className="mono">{det.nic ?? "—"}</dd>
          <dt>Dirección</dt>    <dd>{det.direccion ?? <span className="muted">—</span>}</dd>
          <dt>Asunto</dt>       <dd>{det.asunto ?? <span className="muted">—</span>}</dd>
          <dt>Estado</dt>       <dd>{det.estado ?? <span className="muted">—</span>}</dd>
          <dt>Paso</dt>         <dd>{det.nombre_paso ?? <span className="muted">—</span>}</dd>
          <dt>Días hábiles</dt> <dd className="mono">{det.dias_habiles ?? "—"}</dd>
          <dt>Radicación</dt>   <dd>{det.fecha_radicacion ? formatearFechaCorta(det.fecha_radicacion) : "—"}</dd>
          <dt>Vencimiento</dt>  <dd>{det.fecha_vencimiento ? formatearFechaCorta(det.fecha_vencimiento) : "—"}</dd>
        </dl>
        {det.adjuntos.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Adjuntos ({det.adjuntos.length})
            </div>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", fontSize: 12 }}>
              {det.adjuntos.map((a) => (
                <li key={a.ruta_archivo} style={{ color: "var(--ink-2)", padding: "2px 0" }}>
                  📎 {a.nombre_archivo}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function BloquePerfilWs({
  perfil,
  cargando,
}: {
  perfil?: PerfilClienteWsResponse;
  cargando: boolean;
}) {
  if (cargando) {
    return (
      <div className="card">
        <div className="card__head">
          <h3>Perfil del cliente (WS Afinia)</h3>
        </div>
        <div className="card__body muted">Consultando WS Afinia…</div>
      </div>
    );
  }
  if (!perfil) {
    return (
      <div className="card">
        <div className="card__head">
          <h3>Perfil del cliente (WS Afinia)</h3>
        </div>
        <div className="card__body muted">
          Sin NIC en CRM — no se puede consultar el WS.
        </div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card__head">
        <div className="icon">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 1.5l5 2.5v4.5c0 3-2.5 5-5 6-2.5-1-5-3-5-6V4l5-2.5z" />
          </svg>
        </div>
        <h3>Perfil del cliente · WS Afinia</h3>
      </div>
      <div className="card__body">
        <dl className="kv">
          <dt>Nombre</dt>            <dd>{perfil.nombre ?? <span className="muted">—</span>}</dd>
          <dt>Dirección</dt>         <dd>{perfil.direccion ?? <span className="muted">—</span>}</dd>
          <dt>Estrato</dt>           <dd className="mono">{perfil.estrato ?? "—"}</dd>
          <dt>Factura reclamada</dt> <dd>{perfil.fecha_factura_reclamada ? formatearFechaCorta(perfil.fecha_factura_reclamada) : "—"}</dd>
          <dt>Estado factura</dt>    <dd>{perfil.estado_factura_reclamada ?? <span className="muted">—</span>}</dd>
          <dt>Importe</dt>           <dd className="mono">{formatearCop(perfil.importe_factura_reclamada ?? null)}</dd>
          <dt>Lectura medida</dt>    <dd className="mono">{perfil.lectura_factura ?? "—"}</dd>
          <dt>Consumo reclamado</dt> <dd className="mono">
            {perfil.consumo_reclamado_kwh ? `${perfil.consumo_reclamado_kwh} kWh` : "—"}
          </dd>
          <dt>Histórico</dt>         <dd className="mono">{perfil.historial.length} meses</dd>
        </dl>
        {perfil.lectura_factura_sugerida != null && perfil.lectura_factura == null && (
          <div className="banner" style={{ marginTop: 12 }}>
            <span className="banner__icon">💡</span>
            <div>
              <strong>Lectura sugerida:</strong>{" "}
              {perfil.lectura_factura_sugerida.toLocaleString()} kWh.
              {" "}El medidor no reporta lectura tradicional; calculada como{" "}
              <span className="mono">lectura_anterior + consumo_activa</span>.
              Confírmala en el chat del expediente para que el motor la use.
            </div>
          </div>
        )}
        {perfil.advertencias.length > 0 && (
          <div className="banner" style={{ marginTop: 12 }}>
            <span className="banner__icon">⚠</span>
            <div>{perfil.advertencias.join(" · ")}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function BloqueTextoReclamo({ det }: { det: CrmPqrDetalleResponse }) {
  const fragmentos = [
    det.observaciones,
    det.cuerpo_oficina_virtual && `[OV] ${det.cuerpo_oficina_virtual}`,
    det.observaciones_orden_servicio &&
      `[OS] ${det.observaciones_orden_servicio}`,
  ].filter(Boolean) as string[];
  if (fragmentos.length === 0) return null;
  return (
    <div className="card">
      <div className="card__head">
        <div className="icon">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 3h10v10H3z" />
            <path d="M5 6h6M5 8h6M5 10h4" strokeLinecap="round" />
          </svg>
        </div>
        <h3>Texto del reclamo</h3>
      </div>
      <div
        className="card__body"
        style={{
          fontFamily: "var(--font-serif)",
          fontSize: 14,
          lineHeight: 1.7,
          color: "var(--ink-2)",
          whiteSpace: "pre-wrap",
        }}
      >
        {fragmentos.join("\n\n")}
      </div>
    </div>
  );
}
